import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Modal, Form, Image, Card, Row, Col, Badge } from "react-bootstrap";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { QRCodeCanvas } from "qrcode.react";
import api from "../../api/axios";
import { useAppToast } from "../Common/Toast/AppToastProvider";

const DEFAULT_FORM = {
  name: "",
  description: "",
  price: "",
  discount_type: "",
  discount_percentage: "",
  discount_amount: "",
  max_uses: "",
  status: true,
  qr_code: "",
  image_url: "",
  valid_until: "",
};

const QrProductManager = ({ businessId }) => {
  const { showToast } = useAppToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchProducts = async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      const res = await api.get(`/qr-products/${businessId}`);
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error al traer QR Products:", err);
      showToast("No se pudieron cargar los productos.", "danger", "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    fetchProducts();
  }, [businessId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...products];

    if (q) {
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.qr_code?.toLowerCase().includes(q)
      );
    }

    if (statusFilter === "active") list = list.filter((p) => p.status === "active");
    if (statusFilter === "inactive") list = list.filter((p) => p.status === "inactive");
    if (statusFilter === "expired") list = list.filter((p) => Number(p.is_expired) === 1);

    return list;
  }, [products, query, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, pageSize]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleDiscountTypeChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      discount_type: value,
      discount_percentage: value === "percentage" ? prev.discount_percentage : "",
      discount_amount: value === "fixed" ? prev.discount_amount : "",
    }));
  };

  const handleFileChange = (e) => {
    const nextFile = e.target.files?.[0] || null;
    setFile(nextFile);
    if (nextFile) setPreview(URL.createObjectURL(nextFile));
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setFile(null);
    setPreview(null);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);
      const payload = { ...form, business_id: businessId, status: form.status ? "active" : "inactive" };
      let productId = editingId;

      if (!editingId) {
        payload.qr_code = `qr-${businessId}-${Date.now()}`;
        const res = await api.post("/qr-products", payload);
        productId = res.data.id;
      } else {
        await api.put(`/qr-products/${editingId}`, payload);
      }

      if (file && productId) {
        const formData = new FormData();
        formData.append("image", file);
        await api.post(`/qr-products/${productId}/upload-image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setShowModal(false);
      resetForm();
      await fetchProducts();
      showToast("Beneficio guardado correctamente.", "success", "Listo");
    } catch (err) {
      console.error("Error al guardar QR Product:", err);
      showToast("No se pudo guardar el beneficio.", "danger", "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (prod) => {
    setForm({
      name: prod.name || "",
      description: prod.description || "",
      price: prod.price || "",
      discount_type: prod.discount_type || "",
      discount_percentage: prod.discount_percentage || "",
      discount_amount: prod.discount_amount || "",
      max_uses: prod.max_uses || "",
      status: prod.status === "active",
      qr_code: prod.qr_code || "",
      image_url: prod.image_url || "",
      valid_until: prod.valid_until ? prod.valid_until.split("T")[0] : "",
    });
    setPreview(prod.image_url || null);
    setEditingId(prod.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Estas seguro de eliminar este beneficio?")) return;
    try {
      await api.delete(`/qr-products/${id}`);
      await fetchProducts();
      showToast("Beneficio eliminado.", "success", "Listo");
    } catch (err) {
      console.error("Error al eliminar QR Product:", err);
      showToast("No se pudo eliminar el beneficio.", "danger", "Error");
    }
  };

  if (loading) {
    const skeletonRows = Array.from({ length: 6 });
    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div style={{ height: 24, width: 180, background: "#e5e7eb", borderRadius: 6 }} />
          <div style={{ height: 36, width: 130, background: "#e5e7eb", borderRadius: 999 }} />
        </div>

        <Card className="shadow-sm mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col md={5}>
                <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
              </Col>
              <Col md={3}>
                <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
              </Col>
              <Col md={2}>
                <div style={{ height: 38, background: "#e5e7eb", borderRadius: 6 }} />
              </Col>
              <Col md={2}>
                <div style={{ height: 18, marginTop: 10, background: "#e5e7eb", borderRadius: 6 }} />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Body>
            {skeletonRows.map((_, idx) => (
              <div
                key={idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr",
                  gap: "0.75rem",
                  alignItems: "center",
                  padding: "0.55rem 0",
                  borderBottom: idx === skeletonRows.length - 1 ? "none" : "1px solid #e5e7eb",
                }}
              >
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 12, background: "#e5e7eb", borderRadius: 4 }} />
                <div style={{ height: 20, background: "#e5e7eb", borderRadius: 999 }} />
                <div style={{ height: 44, background: "#e5e7eb", borderRadius: 8 }} />
                <div style={{ height: 44, background: "#e5e7eb", borderRadius: 8 }} />
                <div style={{ height: 30, background: "#e5e7eb", borderRadius: 999 }} />
              </div>
            ))}
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Productos QR</h4>
        <Button
          variant="success"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus className="me-1" /> Nuevo QR
        </Button>
      </div>

      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={5}>
              <Form.Control
                placeholder="Buscar por nombre, descripcion o codigo"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">Todos</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
                <option value="expired">Vencidos</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={10}>10 por pagina</option>
                <option value={20}>20 por pagina</option>
                <option value={30}>30 por pagina</option>
              </Form.Select>
            </Col>
            <Col md={2} className="d-flex align-items-center justify-content-md-end text-muted small">
              {total} resultados
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Table striped bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>Nombre</th>
            <th>Descuento</th>
            <th>Precio</th>
            <th>Vencimiento</th>
            <th>Estado</th>
            <th>Imagen</th>
            <th>QR</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((prod) => (
            <tr key={prod.id}>
              <td>
                <div className="fw-semibold">{prod.name}</div>
                <div className="small text-muted text-truncate" style={{ maxWidth: 220 }}>
                  {prod.description || "-"}
                </div>
              </td>
              <td>
                {prod.discount_type === "percentage" && `-${Number(prod.discount_percentage || 0)}%`}
                {prod.discount_type === "fixed" && `Ahorra $${Number(prod.discount_amount || 0)}`}
                {prod.discount_type === "two_for_one" && "2x1"}
                {!prod.discount_type && "Sin descuento"}
              </td>
              <td>${Number(prod.price || 0).toFixed(2)}</td>
              <td>
                {prod.valid_until ? new Date(prod.valid_until).toLocaleDateString() : "Sin vencimiento"}
              </td>
              <td>
                <Badge bg={prod.status === "active" ? "success" : "secondary"}>
                  {prod.status === "active" ? "Activo" : "Inactivo"}
                </Badge>
              </td>
              <td>
                {prod.image_url ? (
                  <Image src={prod.image_url} alt="producto" width={56} height={56} rounded />
                ) : (
                  "-"
                )}
              </td>
              <td>{prod.qr_code ? <QRCodeCanvas value={prod.qr_code} size={56} /> : "-"}</td>
              <td>
                <Button size="sm" variant="outline-warning" className="me-2" onClick={() => handleEdit(prod)}>
                  <FiEdit />
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(prod.id)}>
                  <FiTrash2 />
                </Button>
              </td>
            </tr>
          ))}
          {pageItems.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center text-muted py-4">
                No hay beneficios para mostrar.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          Mostrando {total === 0 ? 0 : start + 1}-{Math.min(start + pageSize, total)} de {total}
        </small>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </Button>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Editar beneficio QR" : "Nuevo beneficio QR"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleChange} required disabled={saving} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripcion</Form.Label>
              <Form.Control
                name="description"
                value={form.description}
                onChange={handleChange}
                as="textarea"
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Precio base</Form.Label>
              <Form.Control
                name="price"
                type="number"
                step="0.01"
                value={form.price}
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tipo de descuento</Form.Label>
              <Form.Select
                name="discount_type"
                value={form.discount_type || ""}
                onChange={handleDiscountTypeChange}
                disabled={saving}
              >
                <option value="">Sin descuento</option>
                <option value="percentage">% Porcentaje</option>
                <option value="fixed">Monto fijo ($)</option>
                <option value="two_for_one">2x1</option>
              </Form.Select>
            </Form.Group>

            {form.discount_type === "percentage" && (
              <Form.Group className="mb-3">
                <Form.Label>% de descuento</Form.Label>
                <Form.Control
                  name="discount_percentage"
                  type="number"
                  value={form.discount_percentage}
                  onChange={handleChange}
                  disabled={saving}
                />
              </Form.Group>
            )}

            {form.discount_type === "fixed" && (
              <Form.Group className="mb-3">
                <Form.Label>Monto a descontar ($)</Form.Label>
                <Form.Control
                  name="discount_amount"
                  type="number"
                  step="0.01"
                  value={form.discount_amount}
                  onChange={handleChange}
                  disabled={saving}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Maximo de usos</Form.Label>
              <Form.Control
                name="max_uses"
                type="number"
                value={form.max_uses}
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fecha de vencimiento</Form.Label>
              <Form.Control
                type="date"
                name="valid_until"
                value={form.valid_until}
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="status"
                checked={form.status}
                onChange={handleChange}
                label="Activo"
                disabled={saving}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Imagen del producto</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={handleFileChange} disabled={saving} />
            </Form.Group>

            {preview ? (
              <div className="text-center mb-3">
                <Image src={preview} alt="preview" fluid rounded style={{ maxHeight: "200px" }} />
              </div>
            ) : null}

            {form.qr_code ? (
              <div className="text-center mb-3">
                <QRCodeCanvas value={form.qr_code} size={120} />
              </div>
            ) : null}

            <Button type="submit" variant="success" disabled={saving}>
              {saving ? "Guardando..." : editingId ? "Actualizar" : "Guardar"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default QrProductManager;
